import React, { useEffect, useRef, useState, useCallback } from "react";
import { useField } from "@unform/core";
import { MdError } from "react-icons/md";

import {
  Container,
  Error,
  Label,
  LabelContainer,
  RootContainer,
  InputRow,
  AsteriskText,
} from "./styles";
import InputMask from "react-input-mask";

import {
  cnpjMask,
  cpfMask,
  genericDocumentMask,
  passportMask,
} from "../../utils/mask";

const CpfCnpj = ({
  name,
  containerStyle = {},
  icon: Icon,
  validationBlur,
  disabled,
  personType,
  placeholder,
  labelOrientation,
  labelWidth,
  hasLabel,
  required,
  ...rest
}) => {
  const inputRef = useRef(null);

  const [mask, setMask] = useState(genericDocumentMask);
  const [maskChar, setMaskChar] = useState();
  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);

  const { fieldName, defaultValue, error, registerField } = useField(name);

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);

    setIsFilled(!!inputRef.current?.value);

    if (validationBlur) {
      validationBlur(fieldName, inputRef.current?.value);
    }
  }, [fieldName, validationBlur]);

  const handleInputChange = useCallback(
    (e) => {
      // Converte para maiúsculas: CNPJ (J) e Passaporte (I)
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const originalValue = e.target.value;
      const upperValue = originalValue.toUpperCase();

      if (originalValue !== upperValue) {
        e.target.value = upperValue;

        // Preserva posição do cursor
        requestAnimationFrame(() => {
          e.target.setSelectionRange(start, end);
        });
      }
    },
    [personType],
  );

  const handleKeyDown = useCallback(
    (e) => {
      // Permite apenas alfanuméricos: CNPJ (J) e Passaporte (I)
      if (personType === "J" || personType === "I") {
        const allowedKeys = [
          "Backspace",
          "Delete",
          "Tab",
          "Escape",
          "Enter",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
        ];

        if (!allowedKeys.includes(e.key) && !/^[a-zA-Z0-9]$/.test(e.key)) {
          e.preventDefault();
        }
      }
    },
    [personType],
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: "value",
    });
  }, [fieldName, registerField]);

  useEffect(() => {
    if (personType === "J") {
      setMask(cnpjMask);
      setMaskChar(null); // null permite alfanuméricos
    } else if (personType === "F") {
      setMask(cpfMask);
      setMaskChar("_");
    } else if (personType === "I") {
      setMask(passportMask);
      setMaskChar(null);
    } else {
      setMask(genericDocumentMask);
      setMaskChar(null);
    }
  }, [personType]);

  useEffect(() => {
    // Normaliza valores existentes para maiúsculas: CNPJ (J) e Passaporte (I)
    if ((personType === "J" || personType === "I") && inputRef.current?.value) {
      const currentValue = inputRef.current.value;
      const upperValue = currentValue.toUpperCase();
      if (currentValue !== upperValue) {
        inputRef.current.value = upperValue;
        setIsFilled(!!upperValue);
      }
    }
  }, [personType]);

  return (
    <RootContainer labelOrientation={labelOrientation}>
      <InputRow labelOrientation={labelOrientation}>
        {hasLabel && (
          <LabelContainer labelWidth={labelWidth}>
            <Label>{placeholder}</Label>
            {required && <AsteriskText>*</AsteriskText>}
          </LabelContainer>
        )}
        <Container
          style={containerStyle}
          isFocused={isFocused}
          isFilled={isFilled}
          isErrored={!!error}
          disabled={disabled}
          data-testid="input-container"
        >
          {Icon && <Icon size={24} />}
          <InputMask
            mask={mask}
            maskChar={maskChar}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            onInput={handleInputChange}
            onKeyDown={handleKeyDown}
            defaultValue={defaultValue}
            ref={inputRef}
            disabled={disabled}
            placeholder={placeholder}
            {...rest}
          />
        </Container>
      </InputRow>
      {error && (
        <Error
          labelWidth={labelWidth}
          labelOrientation={labelOrientation}
          title={error}
        >
          <MdError color="#e00000" size={20} />
          {error}
        </Error>
      )}
    </RootContainer>
  );
};

export default CpfCnpj;
